import { Component, createElement } from 'react';
import PropTypes from 'prop-types';
import {
  hrefFromLocation,
  isSameHost
} from '@seek/seek-jobs-seo/build/location';
import { isSeekLocation } from '@seek/seek-jobs-seo';
import { qualifyLocation, isQualifiedLocationNoFollow } from './seo';
import hoistNonReactStatic from 'hoist-non-react-statics';
import { uniq } from 'lodash';

const isCurrentLocation = (location, currentLocation) => {
  const homePage = `${currentLocation.hostname}/`;

  const [newURL, currentURL] = [location, currentLocation].map(loc => {
    const path = loc.pathname || '/';
    const host = loc.hostname || currentLocation.hostname;
    return host + path;
  });

  if (newURL === homePage) {
    return false;
  }

  return newURL === currentURL;
};

const calculateRel = (qualifiedLocation, currentLocation, providedRel) => {
  const noFollowRequired =
    isQualifiedLocationNoFollow(qualifiedLocation, currentLocation) ||
    isCurrentLocation(qualifiedLocation, currentLocation);

  const noFollowNotPresent =
    typeof providedRel === 'string' && providedRel.indexOf('nofollow') === -1;

  const shouldAddNoFollow = noFollowRequired && noFollowNotPresent;

  const shouldNoOpen = !isSeekLocation(qualifiedLocation);

  return (
    uniq([
      ...(shouldNoOpen ? ['noopener', 'noreferrer'] : []),
      ...(shouldAddNoFollow ? ['nofollow'] : []),
      ...(providedRel ? providedRel.split(' ') : [])
    ]).join(' ') || null
  );
};

// baseLocation must be enriched
export const useRelative = (baseLocation, targetLocation) =>
  isSameHost(baseLocation, targetLocation) || !targetLocation.hostname;

export function getQualifiedLocation(locale, currentLocation, newLocation) {
  if (useRelative(currentLocation, newLocation) && !newLocation.pathname) {
    return { ...currentLocation, hash: '#' };
  }
  return qualifyLocation(locale, newLocation, currentLocation);
}

export function provideQualifiedLinkParams(WrappedComponent) {
  class WithQualifiedLocation extends Component {
    static contextTypes = {
      store: PropTypes.shape({
        getState: PropTypes.func.isRequired
      }).isRequired
    };

    static propTypes = {
      rel: PropTypes.string
    };

    static defaultProps = {
      rel: ''
    };

    constructor(props) {
      super(props);
      this.getQualifiedLinkParams = this.getQualifiedLinkParams.bind(this);
    }

    // turn a location into {href, rel}
    getQualifiedLinkParams(newLocation) {
      const { store } = this.context;
      const currentLocation = store.getState().location;
      const { locale } = currentLocation;
      const qualifiedLocation = getQualifiedLocation(
        locale,
        currentLocation,
        newLocation
      );
      const href = hrefFromLocation(
        qualifiedLocation,
        null,
        useRelative(currentLocation, qualifiedLocation)
      );
      const rel = calculateRel(
        qualifiedLocation,
        currentLocation,
        this.props.rel
      );
      return { href, rel };
    }

    render() {
      const mergedProps = {
        ...this.props,
        getQualifiedLinkParams: this.getQualifiedLinkParams
      };
      return createElement(WrappedComponent, mergedProps);
    }
  }
  return hoistNonReactStatic(WithQualifiedLocation, WrappedComponent);
}
